import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, CheckCircle2, Loader2, XCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CheckinPage = () => {
  const { guestId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [guest, setGuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGuest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestId, token]);

  const fetchGuest = async () => {
    if (!token) {
      setError('Invalid check-in link');
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/checkin/${guestId}?token=${token}`);
      setGuest(data);
    } catch (err) {
      setError('Invalid or expired check-in link');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    setCheckingIn(true);
    try {
      await axios.post(`${API_URL}/api/checkin/${guestId}?token=${token}`);
      toast.success('Check-in successful!');
      fetchGuest();
    } catch (err) {
      toast.error('Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
        <Card className="w-full max-w-md" data-testid="checkin-error-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
            </div>
            <CardTitle className="text-2xl font-serif">Invalid Link</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const alreadyCheckedIn = guest?.checked_in;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1781947486682-aafbb8427889)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/60 to-black/40" />
      <Card className="w-full max-w-md relative z-10 shadow-[0_8px_30px_rgba(139,30,65,0.3)]" data-testid="checkin-card">
        <CardHeader className="text-center pb-6">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Heart className="h-8 w-8 text-primary" fill="currentColor" />
            </div>
          </div>
          <CardTitle className="text-3xl font-serif tracking-tight">
            {guest?.wedding_bride} & {guest?.wedding_groom}
          </CardTitle>
          <CardDescription className="uppercase tracking-[0.2em] text-xs mt-2">Wedding Check-in</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center border-y border-border py-6">
            <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">Guest Name</p>
            <h2 className="text-2xl font-serif font-semibold" data-testid="checkin-guest-name">{guest?.name}</h2>
            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>Party of {guest?.guests_count}</span>
            </div>
            {guest?.dietary_restrictions && (
              <p className="text-xs text-muted-foreground mt-2">Dietary: {guest.dietary_restrictions}</p>
            )}
          </div>

          {alreadyCheckedIn ? (
            <div className="text-center space-y-3" data-testid="already-checked-in">
              <div className="flex justify-center">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-green-700 dark:text-green-400">Already Checked In</h3>
              <p className="text-sm text-muted-foreground">
                Checked in at {new Date(guest.checked_in_at).toLocaleString()}
              </p>
            </div>
          ) : (
            <Button
              onClick={handleCheckin}
              disabled={checkingIn}
              className="w-full rounded-full h-12 text-base transition-transform hover:-translate-y-0.5"
              data-testid="checkin-button"
            >
              {checkingIn ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Checking In...</>
              ) : (
                <><CheckCircle2 className="h-5 w-5 mr-2" /> Confirm Check-in</>
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
